import type { ErrorEvent } from "@sentry/nextjs";

type EventWithLooseMetadata = ErrorEvent & {
  title?: unknown;
  metadata?: Record<string, unknown>;
};

function stringPart(value: unknown): string {
  return value == null ? "" : String(value);
}

function requestUrlPart(event: ErrorEvent): string {
  return event.request ? event.request.url || "" : "";
}

function exceptionSearchParts(event: ErrorEvent): string[] {
  return (event.exception?.values ?? []).flatMap((exception) => [
    stringPart(exception.type),
    stringPart(exception.value),
  ]);
}

function eventMetadataSearchParts(event: ErrorEvent): string[] {
  const looseEvent = event as EventWithLooseMetadata;
  const metadata = looseEvent.metadata || {};
  return [
    event.message || "",
    requestUrlPart(event),
    stringPart(looseEvent.title),
    stringPart(metadata.title),
    stringPart(metadata.message),
    stringPart(metadata.value),
  ];
}

function breadcrumbSearchParts(event: ErrorEvent): string[] {
  const parts: string[] = [];
  for (const breadcrumb of event.breadcrumbs ?? []) {
    parts.push(breadcrumb.category ?? "", breadcrumb.message ?? "");
    if (breadcrumb.data) {
      parts.push(...Object.values(breadcrumb.data).map((value) => String(value)));
    }
  }
  return parts;
}

export function eventSearchText(event: ErrorEvent): string {
  return [
    ...exceptionSearchParts(event),
    ...eventMetadataSearchParts(event),
    ...breadcrumbSearchParts(event),
  ]
    .join(" ")
    .toLowerCase();
}

export function hasKnownHost(text: string, hosts: readonly string[]): boolean {
  return hosts.some((host) => text.includes(host));
}
